import moment from "moment";
import loading from "@/components/Loading.vue";
import popup from "@/components/AdvertisementModal.vue";
import customerService from "@/components/CustomerService.vue";
import { setCookie, removeCookie } from "@/plugin/cookie";
import {
	createRouter,
	createMemoryHistory,
	createWebHistory,
} from 'vue-router';
import isServer from "@/_base/isServer";
// import setUserObject from "@/plugin/insider-user-object.js";

const history = isServer ? createMemoryHistory("") : createWebHistory("");

// LINE + Facebook in-app browser redirect guard
// LINE: uses official openExternalBrowser=1 param (iOS + Android)
// Facebook Android: uses intent:// scheme to open in default browser
// Facebook iOS: cannot auto-redirect; handled per-page with an overlay
const carbonInAppBrowserGuard = (to, from, next) => {
	if (isServer) return next();
	const ua = navigator.userAgent;
	if (/Line\//i.test(ua)) {
		if (!to.query.openExternalBrowser) {
			const url = window.location.href;
			window.location.replace(url + (url.includes('?') ? '&' : '?') + 'openExternalBrowser=1');
			return;
		}
		return next();
	}
	if (/Android/i.test(ua) && /FBAN|FBAV|FB_IAB/i.test(ua)) {
		window.location.replace('intent://' + window.location.href.replace(/https?:\/\//, '') + '#Intent;scheme=https;action=android.intent.action.VIEW;end;');
		return;
	}
	next();
};

const createRoutes = (store, vue) => {
	return [
		{
			name: 'App',
			path: '/',
			components: {
				loading,
				popup,
				customerService,
				default: () => import('../layout/AppLayout.vue'),
			},
			children: [
				{
					name: 'Index',
					path: '/',
					component: () => import('../pages/Index.vue'),
				},
				{
					path: '/member',
					meta: { hideCustomerService: true },
					redirect: { name: 'Login' },
					component: () => import('../layout/MemberLayout.vue'),
					children: [
						{
							path: 'login',
							name: 'Login',
							component: () => import('../pages/member/Login.vue'),
							beforeEnter: (to, from, next) => {
								// 檢查是否有 from 或 code 參數(社群登入相關)
								if (to.query.from || to.query.code) {
									// 保留 store 參數在 query 中
									const queryParams = {};
									if (to.query.store) {
										queryParams.store = to.query.store;
									}
									next({
										name: "Login",
										query: queryParams,
										state: {
											to: to.params.to,
											from: to.query.from,
											code: to.query.code,
										},
									});
								} else {
									next();
								}
							},
						},
						{
							path: 'signup',
							name: 'Signup',
							component: () => import('../pages/member/Signup.vue'),
						},
						{
							path: 'phoneVerify',
							name: 'PhoneVerify',
							component: () => import('../pages/member/PhoneVerify.vue'),
						},
						{
							path: 'info',
							name: 'MemberInfo',
							component: () => import('../pages/member/Info.vue'),
							beforeEnter: (to, from, next) => {
								if (Object.keys(to.query).length) {
									next({
										name: "MemberInfo",
										state: {
											from: to.query.from,
											code: to.query.code,
										},
									});
								} else {
									next();
								}
							},
						},
						{
							path: 'winning',
							name: 'MemberWinning',
							component: () => import('../pages/member/Winning.vue'),
							beforeEnter: (to, from, next) => {
								if (Object.keys(to.query).length) {
									next({
										name: "MemberWinning",
										state: {
											from: to.query.from,
											code: to.query.code,
										},
									});
								} else {
									next();
								}
							},
						},
						{
							path: 'lineBind',
							name: 'LineBind',
							component: () => import('../pages/member/LineBind.vue'),
						},
						{
							path: 'CarrierBind',
							name: 'CarrierBind',
							component: () => import('../pages/member/CarrierBind.vue'),
						},
					],
				},
				{
					path: '/password',
					meta: { hideCustomerService: true },
					redirect: { name: 'PasswordModify' },
					component: () => import('../layout/MemberLayout.vue'),
					children: [
						{
							path: 'modify',
							name: 'PasswordModify',
							component: () => import('../pages/member/PasswordModify.vue'),
						},
						{
							path: 'forget',
							name: 'PasswordForgot',
							component: () => import('../pages/member/PasswordForgot.vue'),
						},
						{
							path: 'reset',
							name: 'PasswordReset',
							component: () => import('../pages/member/PasswordReset.vue'),
						},
					],
				},
				{
					path: '/cart',
					meta: { hideCustomerService: true },
					redirect: { name: 'CartStep1' },
					component: () => import('../layout/CartLayout.vue'),
					children: [
						{
							path: 'step1',
							name: 'CartStep1',
							component: () => import('../pages/cart/Step1.vue'),
						},
						{
							path: 'step2',
							name: 'CartStep2',
							component: () => import('../pages/cart/Step2.vue'),
							beforeEnter: async (to, from, next) => {
								if (!store.state.headerStore.cartNum) {
									next({ name: "CartStep1" });
									return;
								} else if (store.state.headerStore.cartData?.optional) {
									for (const optional of Object.values(store.state.headerStore.cartData.optional)) {
										let totalNum = 0;
										if (optional.selectedAward) {
											totalNum = optional.selectedAward.reduce((sum, product) => sum + product.num, 0);
										}
										if (optional.match.length !== 0) {
											next({ name: "CartStep1" });
											return;
										} else if (optional.beSelecteds && optional.beSelecteds.max !== totalNum) {
											next({ name: "CartStep1" });
											return;
										}
									}
									next();
								} else {
									next();
								}
							},
						},
						{
							path: 'success/:order',
							name: 'CartSuccess',
							component: () => import('../pages/cart/Success.vue'),
						},
					],
				},
				{
					path: '/order',
					meta: { hideCustomerService: true },
					redirect: { name: 'OrderList' },
					component: () => import('../layout/MemberLayout.vue'),
					children: [
						{
							path: 'list',
							name: 'OrderList',
							component: () => import('../pages/order/List.vue'),
						},
						{
							path: 'detail/:order',
							name: 'OrderDetail',
							component: () => import('../pages/order/Detail.vue'),
						},
						{
							path: 'ask/:order',
							name: 'OrderAsk',
							component: () => import('../pages/order/Ask.vue'),
						},
						{
							path: 'return/:order',
							name: 'OrderReturn',
							component: () => import('../pages/order/Return.vue'),
						},
						{
							path: 'returnProcess/:order',
							name: 'OrderReturnProcess',
							component: () => import('../pages/order/ReturnProcess.vue'),
						},
						{
							path: 'invoice/:order',
							name: 'OrderInvoice',
							component: () => import('../pages/order/Invoice.vue'),
						},
					],
				},
				{
					path: '/category/:cid',
					name: 'Category',
					component: () => import('../pages/Category.vue'),
				},
				{
					path: '/product/:productId',
					name: 'Product',
					component: () => import('../pages/Product.vue'),
				},
				{
					path: '/subscribe',
					name: 'Subscribe',
					component: () => import('../pages/Subscribe.vue'),
				},
				{
					path: '/activity',
					name: 'Activity',
					component: () => import('../pages/Activity.vue'),
				},
				{
					path: '/optional/:optionalId',
					name: 'Optional',
					component: () => import('../pages/Optional.vue'),
				},
				{
					path: '/promotion/:promotionId',
					name: 'Promotion',
					component: () => import('../pages/Promotion.vue'),
				},
				{
					path: '/search/:query',
					name: 'Search',
					component: () => import('../pages/Search.vue'),
				},
				{
					path: '/faq/:qId?',
					name: 'FAQ',
					component: () => import('../pages/FAQ.vue'),
				},
				{
					path: '/contact',
					name: 'Contact',
					component: () => import('../pages/Contact.vue'),
				},
				{
					path: '/news',
					name: 'News',
					component: () => import('../pages/News.vue'),
				},
				{
					path: '/page/:pageName',
					name: 'Page',
					component: () => import('../pages/Page.vue'),
				},
				{
					path: '/:pathMatch(.*)*',
					name: 'NotFound',
					component: () => import('../pages/NotFound.vue'),
				},
			],
		},
		{
			path: '/plusPayQRcode/:orderId',
			name: 'PlusPayQRcode',
			component: () => import('../pages/PlusPayQRcode.vue'),
		},
		{
			path: '/carbon-year',
			name: 'CarbonYear',
			component: () => import('../pages/event/CarbonYear.vue'),
			beforeEnter: carbonInAppBrowserGuard,
		},
		{
			path: '/carbon-year/share/:token',
			name: 'CarbonYearShare',
			component: () => import('../pages/event/CarbonYearShare.vue'),
			beforeEnter: carbonInAppBrowserGuard,
		},
		{
			path: '/carbon-cert',
			name: 'CarbonCert',
			component: () => import('../pages/event/CarbonCert.vue'),
			beforeEnter: carbonInAppBrowserGuard,
		},
		{
			path: '/carbon-cert/share/:token',
			name: 'CarbonCertShare',
			component: () => import('../pages/event/CarbonCertShare.vue'),
			beforeEnter: carbonInAppBrowserGuard,
		},
	];
}

const addSource = ({ source, type, storage }) => {
	let sourceObj = {
		date: moment().format("yyyy/MM/DD, HH:mm:ss"),
	}
	sourceObj[type] = source;
	let sourceArray = storage.getStorageSync("source");
	if (sourceArray && sourceArray.length) {
		storage.setStorageSync("source", [...sourceArray, sourceObj]);
	} else {
		storage.setStorageSync("source", [sourceObj]);
	}
}

export default function (store, vue) {
	const router = createRouter({
		routes: createRoutes(store, vue),
		history,
		scrollBehavior(to, from, savedPosition) {
			return new Promise(async (resolve, reject) => {
				if (to.name === "Category") {
					if (to.params.cid !== from.params.cid) {
						await store.dispatch("categoriesProductsStore/clearCategoriesList");
					}
					await store.dispatch("categoriesProductsStore/getCategoriesList", {
						axios: vue.config.globalProperties.axios,
						cid: to.params.cid,
					});
				}
				const scrollOPtions = {
					top: !to.hash || !isServer && !to.query.from && document.querySelector(to.hash)?.offsetTop > window.pageYOffset ? 0 : store.state.headerStore.headerHeight,
					behavior: "smooth",
				}
				if (savedPosition) {
					resolve(savedPosition);
				} else {
					resolve(to.hash ? Object.assign(scrollOPtions, { el: to.hash }) : scrollOPtions);
				}
			});
		},
	});

	const loggedInRoute = Object.freeze({
		Login: "MemberInfo",
		Signup: "MemberInfo",
		PasswordForgot: "PasswordModify",
		PasswordReset: "PasswordModify",
	});
	const loggedOutRoute = Object.freeze({
		// 訪客結帳：未登入者可進入 Step2/Success（不再導回 Step1），見 guest-checkout D8
		MemberInfo: "Login",
		MemberWinning: "Login",
		OrderList: "Login",
		OrderDetail: "Login",
		OrderAsk: "Login",
		OrderReturn: "Login",
		OrderReturnProcess: "Login",
		PasswordModify: "Login",
	});
	router.beforeEach(async (to, from, next) => {
		if (!isServer) {
			const axios = vue.config.globalProperties.axios;
			const storage = vue.config.globalProperties.$storage;
			const userState = await store.dispatch("headerStore/getUserState", {
				axios,
				cartId: storage.getStorageSync("cartId"),
			});
			// setUserObject(userState?.data?.user);
			if (to.query.ecid) {
				storage.setStorageSync("lineshop_ecid", to.query.ecid, 60 * 60 * 24);
				// 使用 cookie.js 設置 cookie，過期時間為 24 小時
				setCookie('lineshop_ecid', to.query.ecid, { expires: 1 });
			}
		}
		// if ('serviceWorker' in navigator) {
		// 		if(to.name !== from.name) {
		// 			const reg = store.state.deviceStore.reg
		// 			if(reg) {
		// 				store.commit("deviceStore/setOnLine"); // 為了不讓直接進來的時候也重整，所以寫個假如是在 online 時，updated() 才更新 
		// 				reg.update();
		// 			}
		// 		}
		// 	}
		if (to.name in loggedInRoute || to.name in loggedOutRoute) {
			if (to.name in loggedInRoute && store.state.headerStore.loggedIn) {
				next({ name: loggedInRoute[to.name] });
			} else if (to.name in loggedOutRoute && !store.state.headerStore.loggedIn) {
				next({ name: loggedOutRoute[to.name] });
			} else {
				next();
			}
		} else {
			next();
		}
	});
	router.afterEach((to, from, failure) => {
		if (!isServer) {
			store.dispatch("pageLoadingStore/closeLoading");
			const storage = vue.config.globalProperties.$storage;
			if (to.query.r) {
				/* LINE 購物廣告 id 放 cookie */
				if (Number(to.query.r) === 8791) {
					setCookie('lineshop_id', to.query.r);
				} else {
					removeCookie('lineshop_id');
				}
				/* LINE 購物 id 放 cookie END */
				addSource({ source: Number(to.query.r), type: "redirect", storage });
			} else if (!to.query.from || !history.state.from) {
				if (to.query.from) {
					document.__defineGetter__("referrer", () => { return });
				}
				if (document.referrer && new URL(document.referrer).hostname !== location.hostname) {
					addSource({ source: document.referrer, type: "referrer", storage });
				}
			}
			document.__defineGetter__("referrer", () => { return });
		}
	});
	return router;
}
